import {
	queryOptions,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { getGrammarItem } from "../server/functions/learn";
import {
	type ExampleSentence,
	generateExampleSentenceUsingAi,
} from "../server/functions/vocab";
import type { GrammarContent, Source } from "../server/db/schema/flashcard-schema";

type GenerateAiExampleVariables = {
	data: {
		word: string;
		readings: string[];
		meaning: { meaning: string; example: string };
		level: string;
		grammarId: string;
	};
};

export const grammarQueryKeys = {
	all: ["grammar"] as const,
	detail: (id: string) => [...grammarQueryKeys.all, "detail", id] as const,
	examples: (id: string) =>
		[...grammarQueryKeys.detail(id), "examples"] as const,
};

export const grammarItemQueryOptions = (grammarId: string) =>
	queryOptions({
		queryKey: grammarQueryKeys.detail(grammarId),
		queryFn: () => getGrammarItem({ data: { grammarId } }),
		enabled: !!grammarId,
	});

export const grammarExamplesQueryOptions = (grammar: Source | null) =>
	queryOptions({
		queryKey: grammarQueryKeys.examples(grammar?.sid ?? ""),
		queryFn: async (): Promise<ExampleSentence[]> => {
			if (!grammar) return [];

			const grammarContent = grammar.content as GrammarContent;

			if (grammarContent.phrases && grammarContent.phrases.length > 0) {
				return grammarContent.phrases.map(
					(e): ExampleSentence => ({
						sentence: e.phrase,
						source: "https://github.com/Ronokof/Full-Japanese-Study-Deck",
						translation: e.translation,
					}),
				);
			}

			return [];
		},
	});

export const useGrammarItem = (grammarId: string) =>
	useSuspenseQuery(grammarItemQueryOptions(grammarId));

export const useGrammarSentenceExamples = (grammar: Source | null) =>
	useSuspenseQuery(grammarExamplesQueryOptions(grammar));

export const useGenerateAIExample = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (vars: GenerateAiExampleVariables) => {
			const result = await generateExampleSentenceUsingAi({
				data: {
					word: vars.data.word,
					readings: vars.data.readings ?? [],
					meaning: vars.data.meaning,
					level: vars.data.level,
				},
			});

			if ("error" in result) {
				throw new Error(result.error);
			}

			return result;
		},
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: grammarQueryKeys.examples(variables.data.grammarId),
			});
		},
	});
};
